import { router, useLocalSearchParams, type Href } from 'expo-router';
import { Platform } from 'react-native';

/**
 * Same-origin path predicate. Rejects:
 *   - absolute URLs (`https://evil.com`)
 *   - protocol-relative URLs (`//evil.com`)
 *   - empty / non-path strings
 *
 * The `//` rejection is critical: `router.push('//evil.com')` happily
 * navigates off-origin on web.
 */
const isSafeInternalPath = (p: unknown): p is string =>
	typeof p === 'string' && p.startsWith('/') && !p.startsWith('//');

export interface UseBackToResult {
	/** Sanitized backTo from `?backTo=` (or legacy `?from=`), or undefined. */
	backTo: string | undefined;
	/**
	 * Replace-navigate to backTo when set. Otherwise fall back to
	 * `router.back()` if there's a real back entry, else no-op.
	 */
	goBack: () => void;
	/**
	 * Build a Href that points to `href` with the current URL encoded as a
	 * `?backTo=` query param. Use when navigating away from a report so the
	 * drilled page can return to the exact filter state.
	 */
	withBackTo: (href: string) => Href;
}

/**
 * Back-navigation convention for reports and detail pages. The drilled page
 * reads `?backTo=` and exposes `goBack()` for its back link; the originating
 * page wraps drill-down hrefs in `withBackTo(href)` so the round-trip is
 * lossless across refresh + deep links.
 *
 * Legacy `?from=` is accepted for backward compat with pages already using
 * that name; new code should emit `?backTo=`.
 */
export const useBackTo = (): UseBackToResult => {
	const params = useLocalSearchParams<{ backTo?: string; from?: string }>();
	const raw = params.backTo ?? params.from;
	const backTo = isSafeInternalPath(raw) ? raw : undefined;

	const goBack = (): void => {
		if (backTo) {
			router.replace(backTo as Href);
			return;
		}
		if (router.canGoBack()) router.back();
	};

	const withBackTo = (href: string): Href => {
		const here = Platform.OS === 'web' && typeof window !== 'undefined'
			? `${window.location.pathname}${window.location.search}`
			: '';
		if (!here || !isSafeInternalPath(here)) return href as Href;
		const sep = href.includes('?') ? '&' : '?';
		return `${href}${sep}backTo=${encodeURIComponent(here)}` as Href;
	};

	return { backTo, goBack, withBackTo };
};
